import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ListsHttpService } from '../../../services/http-services/lists/lists-http.service';
import { PAGES_AND_PATHS } from '../../../constants/pages-and-paths';
import { Location } from '@angular/common';
import { ListInterface } from 'src/app/services/http-services/lists/lists-http-intterfaces';
import {AvailableLanguages} from '../../../constants/translate-variables';

@Component({
  selector: 'tl-create-list',
  templateUrl: './create-list.component.html',
})
export class CreateListComponent implements OnInit {
  MAX_FILE_SIZE = 10 * 1024 * 1024;
  formTriedToSubmit = false;
  isSpinnerActive = false;
  createListForm!: FormGroup;
  coverImage: any = null;
  isMaxSizePhotoError = false;
  isRemoveImageModalOpen = false;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  listId = 0;
  list: ListInterface | null = null;
  historyState: any;
  isOpenToastOpen = false;
  selectedListAndItem: any;

  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private toastService: ToastrService,
              private listsHttpService: ListsHttpService,
              private location: Location,
              private actRoute: ActivatedRoute) { }

  ngOnInit(): void {
    this.historyState = history.state;
    this.buildForm();
    this.actRoute.paramMap.subscribe(params => {
      this.listId = Number(params.get('id') || '');
      if (this.listId) {
        this.getSingleList(this.listId);
      }
    });
  }

  buildForm(): void {
    this.createListForm = this.formBuilder.group({
      title: ['', [Validators.required, Validators.maxLength(50)]],
      cover_image: [null],
      visibility: [0],
      locales: []
    });
  }

  triggerFileInput(event: Event): void {
    event.preventDefault();
    this.fileInput.nativeElement.click();
  }

  onFileChange(event: any): void {
    const file = event.target.files[0];
    if (file?.size > this.MAX_FILE_SIZE) {
      this.isMaxSizePhotoError = true;
      this.coverImage = null;
      this.createListForm.patchValue({ cover_image: null });
    } else {
      this.isMaxSizePhotoError = false;
      this.createListForm.patchValue({ cover_image: file });
      this.coverImage = URL.createObjectURL(file);
    }
    this.fileInput.nativeElement.value = '';
  }

  onConfirmRemoveFile(): void {
    this.coverImage = null;
    this.createListForm.patchValue({ cover_image: null });
    this.isRemoveImageModalOpen = false;
  }

  onSubmit(): void {
    this.createListForm.controls.locales.patchValue(AvailableLanguages.map(lang => lang.name));
    this.list === null ? this.createList() : this.updateList();
  }

  createList(): void {
    this.formTriedToSubmit = true;
    if (this.createListForm.valid) {
      this.isSpinnerActive = true;
      const {cover_image, ...reqBody} = this.createListForm.value;
      reqBody.visibility = this.createListForm.controls.visibility.value ? 0 : 1;
      this.listsHttpService.postList(reqBody).then(res => {
        if (res.code === 200) {
          if (this.createListForm.controls.cover_image.value) {
            const formData = new FormData();
            const coverImage = this.createListForm.get('cover_image')?.value;
            this.createListForm.patchValue({ cover_image: this.coverImage });
            formData.append('cover_image', coverImage, coverImage.name);
            this.listsHttpService.postListImage({listId: res.data.id}, formData).then((uploadRes: any) => {
            });
          }
          const successPageDetails = {
            iconPath: '../../../../assets/icons/outline/icon-successfully-screen1.svg',
            title: '',
            description: 'Your list has been successfully created!',
            btnText: 'Add items to your list',
            redirectToUrl: `/single-list/${res.data.id}/cards`
          };
          this.isSpinnerActive = false;
          this.formTriedToSubmit = false;
          this.coverImage = null;
          this.createListForm.reset();

          if (this.historyState.selectedItem || this.historyState.url) {
            this.isOpenToastOpen = true;
            this.selectedListAndItem = {list: res.data, item: this.historyState.selectedItem};
            this.addItemToList(this.historyState.selectedItem?.id, res.data.id, this.historyState.url);
          } else {
            this.router.navigate([PAGES_AND_PATHS.successAction.pagePath], {state: successPageDetails});
          }
        }
      }).catch(({ error }: { error: { errors: { [key: string]: string } } }) => {
        this.isSpinnerActive = false;
        Object.values(error.errors).forEach(error => this.toastService.error());
      });
    }
  }

  addItemToList(itemId: number, listId: number, url: string): void {
    if (listId !== null && itemId !== null) {
      this.listsHttpService.copyListItem(listId, itemId).subscribe((response: any) => {
        // { state: this.selectedListAndItem }
        this.router.navigateByUrl(url);
      });
    }
  }

  updateList(): void {
    this.formTriedToSubmit = true;
    const formData = new FormData();
    const coverImage = this.createListForm.get('cover_image')?.value;
    this.isSpinnerActive = true;
    this.createListForm.patchValue({ cover_image: this.coverImage });
    formData.append('title', this.createListForm.controls.title.value);
    formData.append('visibility', this.createListForm.controls.visibility.value ? '0' : '1');
    if (coverImage) {
      formData.append('cover_image', coverImage, coverImage.name);
    }

    this.listsHttpService.updateList(formData, this.listId).then(res => {
      this.isSpinnerActive = false;
      this.formTriedToSubmit = false;
      this.coverImage = null;
      this.createListForm.reset();
      this.router.navigate([`/single-list/${res.data.id}/cards`]);
    }).catch(({ error }: { error: { errors: { [key: string]: string } } }) => {
      this.isSpinnerActive = false;
      Object.values(error.errors).forEach(error => this.toastService.error());
    });
  }

  getSingleList(listId: number): void {
    this.listsHttpService.getSingleList(listId).then((res: ListInterface) => {
      if (res) {
        this.list = res;
      }
      this.coverImage = this.list?.lyst_image_url;
      this.createListForm.patchValue({
        cover_image: null,
        title: this.list?.title,
        visibility: this.list?.visibility ? 0 : 1
      });
    });
  }

  goBack(): void {
    this.location.back();
  }
}
